import { Field, FieldInputProps, FieldMetaProps, FieldProps, getIn } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PaloField, PaloFieldProps } from '@synergy/components-palo';
import { ReactElement, ReactNode } from 'react';

const REQUIRED_ERROR = 'app.error.required';

export interface PaloFormikFieldRenderProps<V> {
    field: FieldInputProps<V>;
    meta: FieldMetaProps<V>;
    /** Whether the control should show its error state. */
    hasError: boolean;
    /** Sets this field's value, for controls that report a value rather than an event. Untyped, as in Formik: a control often reports a wider type than the form keeps. */
    setValue: (value: unknown) => void;
}

export interface PaloFormikFieldProps<V> extends Omit<PaloFieldProps, 'name' | 'error' | 'label'> {
    name: string;
    label: ReactNode;
    required?: boolean;
    children: (props: PaloFormikFieldRenderProps<V>) => ReactNode;
}

/**
 * A labelled PALO field bound to one Formik value: it marks unsaved input, adds
 * the required star and shows the validation message once the field is touched
 * — for a missing value, only after a save was tried.
 * The control itself is left to the caller.
 */
export const PaloFormikField = <V,>({
    name,
    label,
    required,
    children,
    ...fieldProps
}: PaloFormikFieldProps<V>): ReactElement => (
    <Field name={name}>
        {({ field, meta, form }: FieldProps<V>) => {
            // A field left empty is not wrong yet: the title of a new inquiry,
            // focused on opening, turned red at the first click elsewhere
            // (R-201). Missing input shows once a save was tried; wrong input
            // as soon as the field is left. A form rebuilt from a failed save
            // brings that attempt along as its initial touched fields.
            const saveTried = form.submitCount > 0 || !!getIn(form.initialTouched, field.name);
            const hasError = !!meta.touched && !!meta.error && (saveTried || REQUIRED_ERROR !== meta.error);
            const setValue = (value: unknown) => form.setFieldValue(field.name, value);

            return (
                <PaloField
                    {...fieldProps}
                    label={
                        <>
                            {label}
                            {required ? ' *' : ''}
                        </>
                    }
                    name={field.name}
                    error={hasError ? <FormattedMessage id={meta.error} /> : undefined}
                >
                    {children({ field, meta, hasError, setValue })}
                </PaloField>
            );
        }}
    </Field>
);
